/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN FEES API
 * Role: Admin
 * Scope: Tenant-based fee management
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINTS
============================================================= */
const ADMIN_FEES_ENDPOINT = "/admin/fees/index.php";
const ADMIN_PAID_LIST_ENDPOINT = "/admin/fees/paid-list.php";
const ADMIN_RECEIPT_GENERATE_ENDPOINT = "/admin/receipts/generate.php";
const ADMIN_RECEIPT_DOWNLOAD_ENDPOINT = "/admin/receipts/download.php";

/* =============================================================
   GET – Fetch All Fees (Pagination + Search)
============================================================= */
export const getAdminFees = async (
  { page = 1, limit = 10, search = "" } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_FEES_ENDPOINT,
      null,
      "Admin – Fetch Fees",
      {
        params: { page, limit, search },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch fees",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {},
    };
  } catch (error) {
    console.error("Admin Fetch Fees Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};

/* =============================================================
   GET – Fetch Paid Fees Only
============================================================= */
export const getAdminPaidFees = async (
  {
    page = 1,
    limit = 10,
    search = "",
  } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_PAID_LIST_ENDPOINT,
      null,
      "Admin – Fetch Paid Fees",
      {
        params: {
          page,
          limit,
          search,
        },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch paid fees",
        data: [],
        pagination: {},
        filters: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      pagination: res.pagination || {},
      filters: res.filters || {},
      message: res.message || "",
    };

  } catch (error) {
    console.error("Admin Fetch Paid Fees Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
      data: [],
      pagination: {},
      filters: {},
    };
  }
};

/* =============================================================
   GET – Fetch Single Fee
============================================================= */
export const getAdminFeeById = async (id, options = {}) => {
  try {
    const res = await apiCall(
      "GET",
      `${ADMIN_FEES_ENDPOINT}?id=${id}`,
      null,
      "Admin – Fetch Fee By ID",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch fee",
      };
    }

    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    console.error("Admin Fetch Fee By ID Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Collect Fee
============================================================= */
export const collectAdminFee = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_FEES_ENDPOINT,
      payload,
      "Admin – Collect Fee",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to collect fee",
      };
    }

    return {
      success: true,
      data: res.data,
      message: res.message,
    };
  } catch (error) {
    console.error("Admin Collect Fee Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   DELETE – Remove Fee Record
============================================================= */
export const deleteAdminFee = async (id, options = {}) => {
  try {
    const res = await apiCall(
      "DELETE",
      `${ADMIN_FEES_ENDPOINT}?id=${id}`,
      null,
      "Admin – Delete Fee",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to delete fee",
      };
    }

    return {
      success: true,
      message: res.message,
    };
  } catch (error) {
    console.error("Admin Delete Fee Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Generate Receipt
============================================================= */
export const generateAdminReceipt = async (feeId, options = {}) => {
  try {
    const res = await apiCall(
      "GET",
      `${ADMIN_RECEIPT_GENERATE_ENDPOINT}?id=${feeId}`,
      null,
      "Admin – Generate Receipt",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to generate receipt",
      };
    }

    return {
      success: true,
      message: res.message,
    };
  } catch (error) {
    console.error("Admin Generate Receipt Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   DOWNLOAD – Receipt (Using apiCall | Enterprise Standard)
============================================================= */
export const downloadAdminReceipt = async (feeId, options = {}) => {
  try {
    const res = await apiCall(
      "GET",
      `${ADMIN_RECEIPT_DOWNLOAD_ENDPOINT}?id=${feeId}`,
      null,
      "Admin – Download Receipt",
      {
        responseType: "blob", // IMPORTANT
        ...options,
      }
    );

    if (!res) {
      throw new Error("Invalid response");
    }

    const blob = new Blob([res], { type: "application/pdf" });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `receipt-${feeId}.pdf`;

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      message: "Receipt downloaded successfully",
    };

  } catch (error) {
    console.error("Admin Download Receipt Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   PUT – Update Fee
============================================================= */
export const updateAdminFee = async (
  id,
  payload,
  options = {}
) => {
  try {

    if (!id) {
      return {
        success: false,
        message: "Invalid Fee ID",
      };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_FEES_ENDPOINT}?id=${id}`,
      payload,
      "Admin – Update Fee",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to update fee",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message: res.message || "Fee updated successfully",
    };

  } catch (error) {

    console.error(
      "Admin Update Fee Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};